import React, { useState, useEffect, useRef, useCallback } from "react";
import { Input, Button } from 'reactstrap';
import './BuiltInTests.scss';

import sentenceText_en from "./resources/grade3Sent_en.js";
import sentenceText_es from "./resources/grade3Sent_es.js";
import sentenceText_fr from "./resources/grade3Sent_fr.js";
import sentenceText_ptbr from "./resources/grade3Sent_ptbr.js";
import wordText_en from "./resources/grade3Word_en.js";
import wordText_es from "./resources/grade3Word_es.js";
import wordText_fr from "./resources/grade3Word_fr.js";
import wordText_ptbr from "./resources/grade3Word_ptbr.js";
import letterText_en from "./resources/letters_en.js";
import letterText_es from "./resources/letters_es.js";
import letterText_fr from "./resources/letters_fr.js";
import letterText_ptbr from "./resources/letters_ptbr.js";

import { calculateAverageDuration, calculateAverageAccuracy, calculateAverageWPM, 
  calculateAverageCorrectWPM, calculateMinimumStringDistance } from "./resources/TypingTestCalcs";
import DisplayTypingResults from "./DisplayTypingResults";
import { TEST_DRIVE_ACTIVITIES, TEXT_LANGUAGES, TEXT_UNITS, 
  DEFAULT_TEXT_LANGUAGE, DEFAULT_TEXT_UNIT, DEFAULT_TEXTS_IN_TEST, NUM_PRACTICE_TRIALS } from "../../Views/ConstForTestDriveSection";

function getBucketOfText(textUnit, language){
  let texts = []; //array of strings
  let lCode = '';

  const availableBuckets = {
    sentence_en : sentenceText_en.split('\n'),
    sentence_es : sentenceText_es.split('\n'),
    sentence_fr : sentenceText_fr.split('\n'),
    sentence_ptbr : sentenceText_ptbr.split('\n'),
    word_en : wordText_en.split('\n'),
    word_es: wordText_es.split('\n'),
    word_fr: wordText_fr.split('\n'),
    word_ptbr: wordText_ptbr.split('\n'),
    letter_en : letterText_en.split('\n'),
    letter_es: letterText_es.split('\n'),
    letter_fr: letterText_fr.split('\n'),
    letter_ptbr: letterText_ptbr.split('\n'),
  }

  switch(language){
    case( TEXT_LANGUAGES.EN):
      lCode = 'en';
      break;
    case( TEXT_LANGUAGES.ES):
      lCode='es';
      break;
    case( TEXT_LANGUAGES.FR):
      lCode='fr';
      break;
    case( TEXT_LANGUAGES.PTBR):
      lCode='ptbr';
      break;
    default:
      lCode = 'en';
      break;
  }
  switch(textUnit){
    case( TEXT_UNITS.SENT):
      texts = availableBuckets[`sentence_${lCode}`];
      break;
    case( TEXT_UNITS.WORD):
      texts = availableBuckets[`word_${lCode}`];
      break;
    case( TEXT_UNITS.LTR):
      texts = availableBuckets[`letter_${lCode}`];
      break;
    default:
      texts = availableBuckets['sentence_en'];
      break;
  }

  console.log("Texts: " + texts);
  return(texts);
}

const generateRandomText = (() => {
  let sentences = ['a', 'b']; //defaults, but this should be initialized with .initializeTexts; sentenceText_en.split('\n')
  let usedIndices = new Set();
  let currentIndex = -1;

  const resetUsedIndices = () => {
    usedIndices = new Set();
  };

  const getRandomSentence = () => {
    if (usedIndices.size === sentences.length) {
      // If all sentences have been used, reset the usedIndices set
      resetUsedIndices();
    }

    let randomIndex;
    do {
      randomIndex = Math.floor(Math.random() * sentences.length);
    } while (randomIndex !== currentIndex && usedIndices.has(randomIndex));
    
    currentIndex = randomIndex
    usedIndices.add(currentIndex);
    return sentences[currentIndex];
  }

  return {
    initializeTexts: (texts) => {
      console.log("initializing Texts");
      sentences = texts;
      usedIndices = new Set();
      currentIndex = -1;
    },
    currentText: () => {
      console.log("calling currentText");
      if(currentIndex < 0){
        return getRandomSentence();
      }else{
        return sentences[currentIndex];
      }
    },
    generate: getRandomSentence,
    reset: resetUsedIndices
  };
})();

const TypingTest = (props) => {
  const {
    runningPractice,
    disableInput,
    finished,         //whether overall test is completed or not.
    setFinished,      //controlled by parent so that there is "one source of truth" and so parent can handle navigation accordingly
    saveActivityResultsToLocalState,    //callback to get results from this test to the parent
    textUnit = DEFAULT_TEXT_UNIT,
    language = DEFAULT_TEXT_LANGUAGE
  } = props;
  let {
    numTrials = DEFAULT_TEXTS_IN_TEST,
    statsToDisplay = [],   //component stats for the test (to display when finished, if you navigate back to the page)
  } = props;

  if(runningPractice)
    numTrials = NUM_PRACTICE_TRIALS;

  const [inputText, setInputText] = useState("");
  const [prevSelectionTime, setPrevSelectionTime] = useState(0);
  const [sentenceTime, setSentenceTime] = useState(0);
  const [totalSelections, setTotalSelections] = useState(0);
  const [completedTests, setCompletedTests] = useState([]);
  const [textToType, setTextToType] = useState("default text");
  const prevTextToTypeRef = useRef(textToType);
  if (completedTests.length > 0){
    statsToDisplay = completedTests;
  }
  console.log("@@@@@ Rendering TypingTest, finished: " + finished + " sentenceTime: " + sentenceTime);

  //Uncomment this for a 'Next' sentence button.
  //used to make sure focus stays on input field -- needed when using button to go to next sentence
  // const inputRef = useRef(null);
  // const focus = () => {
  //   if( inputRef.current){
  //     inputRef.current.focus();
  //   }
  // }

  const handleInputChange = (e) => {
    if(disableInput){
      return;
    }
    const { value } = e.target;
    if(value.slice(-1) === '\n'){
      testCompleted();
    }else{
      var curTime = Date.now();
      setTotalSelections((sel) => sel + 1);
      setInputText(value);

      //duration begins when the first keystroke is hit
      //i.e., doesn't include time required to hit that first keystroke. OK.
      if(prevSelectionTime > 0){
        const timeDifference = curTime - prevSelectionTime;
        setSentenceTime((prev) => prev + timeDifference);
      }
      setPrevSelectionTime(curTime);
    }
  };

  /* calls testCompleted() to capture a partial sentence before /n 
  Sets finished to true, which triggers a useEffect to end the test
  */
  const handleDone = () => {
    testCompleted();
    setFinished(true);
  }
  
  //called when a sentence is completed (by hitting Enter at the end of it)
  //triggers a call to useEffect(), which determines whether to present another sentence or wrap up the test
  function testCompleted(){
    const targetText = textToType; 
    setCompletedTests((current) => [
      ...current, {
        sentence: targetText,
        duration: sentenceTime,
        targetTextLength: targetText.length,
        totalWords: targetText.split(" ").length,
        totalSelections: totalSelections,
        minStringDistance: calculateMinimumStringDistance(inputText, targetText, textUnit === TEXT_UNITS.LTR)
      }]);    
  }

  //wrapped in useCallback b/c it is a dependency for useEffect.
  //https://stackoverflow.com/questions/56410369/can-i-call-separate-function-in-useeffect
  const resetSentence = useCallback(() => {
    setInputText("");
    setSentenceTime(0);
    setPrevSelectionTime(0);
    setTotalSelections(0);
    setTextToType(generateRandomText.generate());
  }, []);

  // if we get rid of this, we can put the resetSentence function inside of useEffect,
  // instead of wrapping in useCallback.
  // but it might have been useful in the sandbox, so maybe just keep it around
  const resetTest = () =>{
    generateRandomText.reset();
    resetSentence();
    setCompletedTests([]);
  }

  const saveTest = useCallback(() => {
    console.log("@@@@ TypingTest.saveTest.");

    //just make sure not to call this with empty data
    //(can happen if Continue from test and then Back to see results)
    if(saveActivityResultsToLocalState && completedTests && completedTests.length > 0){
      saveActivityResultsToLocalState({
        type: TEST_DRIVE_ACTIVITIES.TEXT_ENTRY_BASIC,
        completedTests: completedTests, 
        averageDuration: (calculateAverageDuration(completedTests)/1000).toFixed(2), 
        averageRawWPM: calculateAverageWPM(completedTests).toFixed(2),
        averageCorrectWPM: calculateAverageCorrectWPM(completedTests).toFixed(2),
        averageAccuracy: calculateAverageAccuracy(completedTests).toFixed(2)
      });
    }
  }, [completedTests, saveActivityResultsToLocalState]);

  // to be able to count time before a letter is selected in letter mode
  // activates any time textToType gets set. It sets the previoustime to the time it is set if
  // textToType changes and in letter mode. Always sets the prev value to the current value on activation
  useEffect(() =>{
    if(prevTextToTypeRef.current !== textToType && textUnit === TEXT_UNITS.LTR){
        setPrevSelectionTime(Date.now());
    }
    prevTextToTypeRef.current = textToType;
  }, [textToType]);

  //initialize the text bucket and get the first sentence (only once, on initial render of TypingTest)
  useEffect(() => {
    let texts = getBucketOfText(textUnit, language);
    generateRandomText.initializeTexts(texts);
    setTextToType(generateRandomText.currentText());
  }, [textUnit, language]);

  //called whenever completedTests changes (at first render, and when you hit Enter after a sentence)
  // and whenever 'finished' changes
  useEffect(()=>{
    if((completedTests.length === numTrials) || finished){
      saveTest();
    }
    if (completedTests.length > 0){
      resetSentence();
      // focus(); //uncomment this to use Next button to advance sentence
    }
  }, [completedTests, numTrials, saveTest, finished, resetSentence]);
  
  return (
    <div>
    {!finished && (
      <div>
        <div className="aa-typing-test-content">
          <p>{textToType}</p>
          <Input
            placeholder="Start typing here..."
            value={inputText}
            onChange={handleInputChange}
            disabled={finished}
            type="textarea"
            // innerRef={inputRef}
            autoFocus={true}
          />     
        </div>  
        <div className="aa-review-row"> 
          <span className="float-start aa-more-info">
            {/* <Button className="aa-update-button" onClick={testCompleted}>Next</Button> */}
            Item {completedTests.length + 1} of {numTrials}. Enter for next item.
          </span>
          <Button className="aa-end-activity-btn float-end" onClick={handleDone}>End Activity</Button>
        </div>
      </div>
      )}
    {finished && (<DisplayTypingResults  sentences={statsToDisplay}/>)}
    </div>
  );
};

export default TypingTest;
